import * as Rx from 'rxjs'
import * as log from 'loglevel'
import { WebSocketSubject } from 'rxjs/observable/dom/WebSocketSubject'

export class AlreadySubscribeError extends Error {}

export enum State {
  Connecting,
  Connected,
  Disconnecting,
  Disconnected
}

export default class EventHub {
  /**
   * subject emits when we got disconnected not gracefully
   */
  notGracefulDisconnect = new Rx.Subject<void>()
  /**
   * Observable to emits when there is an message from server
   */
  messages = new Rx.Subject<MessageEvent>()
  /**
   * BehaviorSubject for current state
   */
  state = new Rx.BehaviorSubject(State.Disconnected)

  private logger = log.getLogger('EventHub')
  private subscription = new Rx.Subscription()
  private socket = new Rx.ReplaySubject<WebSocketSubject<MessageEvent>>(1)
  private subscriptions: {
    [channel: string]: {
      lastID: string | null
      observer: Rx.Observer<{}>
    }
  } = {}
  private url: string
  // subject to be triggered when websocket connected
  private openSubject = new Rx.Subject<Event>()
  // subject to be triggered when websocket is closing
  private closingSubject = new Rx.Subject<Event>()
  // subject to be triggered when `connect` is called
  private connectSubject = new Rx.Subject<void>()
  // subject to be triggered when `disconnect` is called
  private disconnectSubject = new Rx.Subject<void>()
  // subject to be triggered when `subscribe` is called
  private subscribeSubject = new Rx.Subject<string>()
  // subject to be triggered when a subscription is canceled
  private unsubscribeSubject = new Rx.Subject<string>()
  // subject to be triggered when `ping` is called
  private pingSubject = new Rx.Subject<string>()

  constructor (url: string) {
    this.url = url

    // log state changes
    this.subscription.add(
      this.state.pairwise().subscribe(pair => {
        const previous = State[pair[0]]
        const current = State[pair[1]]
        this.logger.info(`State change from ${previous} to ${current}`)
      })
    )

    // emit not graceful disconnect subject
    this.subscription.add(
      this.state
        .pairwise()
        .filter(
          pair =>
            pair[0] !== State.Disconnecting && pair[1] === State.Disconnected
        )
        .map(() => {
          this.logger.warn('Not graceful disconnect')
        })
        .subscribe(this.notGracefulDisconnect)
    )

    // create socket when connect method called
    this.subscription.add(
      this.state
        .switchMap(
          state =>
            state === State.Disconnected || state === State.Disconnecting
              ? this.connectSubject
              : Rx.Observable.never<void>()
        )
        .map(() => {
          this.logger.info('Connecting to', this.url)
          this.state.next(State.Connecting)
          return Rx.Observable.webSocket({
            url: this.url,
            // only pass next event, so it won't complete them. For old RxJS-dom
            // these open observer will received next follow with complete.
            // https://github.com/Reactive-Extensions/RxJS-DOM/blob/master/src/dom/websocket.js#L29
            // but for RxJS 5 websocket, it only emits next. even so, we still
            // only receive next here to avoid implementation detail change in the
            // future
            openObserver: Rx.Subscriber.create(
              this.openSubject.next.bind(this.openSubject)
            ),
            closeObserver: Rx.Subscriber.create(
              this.closingSubject.next.bind(this.closingSubject)
            ),
            // do not process the result, so tat we get the raw event
            // Notice: cast as any for fixing the typing issue here
            // https://github.com/ReactiveX/rxjs/issues/2754
            resultSelector: (event: MessageEvent) => event as any
          })
        })
        .subscribe(this.socket)
    )

    // transit state to connected when websocket is connected
    this.subscription.add(
      this.openSubject.subscribe(() => {
        this.state.next(State.Connected)
      })
    )

    // transit state to connected when websocket is connected
    this.subscription.add(
      this.closingSubject.subscribe(() => {
        this.state.next(State.Disconnected)
      })
    )

    const disconnectedSocket = this.socket.sample(this.disconnectSubject)
    // disconnect websocket
    this.subscription.add(
      this.state
        .switchMap(
          state =>
            state === State.Connected || state === State.Connecting
              ? disconnectedSocket
              : Rx.Observable.never<WebSocketSubject<MessageEvent>>()
        )
        .subscribe(socket => {
          this.state.next(State.Disconnecting)
          socket.complete()
        })
    )

    // subscribe socket messages
    this.subscription.add(
      this.socket
        .switchMap(socket => socket.materialize())
        .subscribe(notification => {
          this.messages.next(notification.value)
          switch (notification.kind) {
            case 'N':
              const json = JSON.parse(notification.value.data)
              switch (json.op) {
                case 'update':
                  for (const channel in json.channels) {
                    const update = json.channels[channel]
                    const subscription = this.subscriptions[channel]
                    if (!subscription) {
                      this.logger.warn(
                        'Event hub event updated channel',
                        channel,
                        'without subscription'
                      )
                      continue
                    }
                    // TODO: what if not found
                    for (const event of update.events) {
                      subscription.observer.next(event.payload)
                    }
                    subscription.lastID = update.last_id
                    this.logger.info(
                      `Update channel ${channel} with ` +
                        `${update.events.length} events, ` +
                        `last_id=${update.last_id}`
                    )
                  }
                  break
              }
              break
            case 'E':
              this.logger.error('Websocket error', notification.error)
              break
          }
        })
    )

    // send subscribe when socket opens
    this.subscription.add(
      this.socket.sample(this.openSubject).subscribe(socket => {
        this.logger.info('EventHub connection made', socket)
        let channels: { [key: string]: string | null } = {}
        Object.keys(this.subscriptions).forEach(channel => {
          const subscription = this.subscriptions[channel]
          channels[channel] = subscription.lastID
        })
        socket.next((JSON.stringify({
          op: 'subscribe',
          channels
        }) as any) as MessageEvent)
      })
    )

    // send subscribe when new subscription added
    this.subscription.add(
      this.subscribeSubject
        .withLatestFrom(this.socket, this.state)
        .filter(tuple => tuple[2] === State.Connected)
        .subscribe(([channel, socket, ..._]) => {
          socket.next((JSON.stringify({
            op: 'subscribe',
            channels: { [channel]: '' }
          }) as any) as MessageEvent)
        })
    )

    // send unsubscribe when subscription canceled
    this.subscription.add(
      this.unsubscribeSubject
        .withLatestFrom(this.socket, this.state)
        .filter(tuple => tuple[2] === State.Connected)
        .subscribe(([channel, socket, ..._]) => {
          socket.next((JSON.stringify({
            op: 'unsubscribe',
            channels: [channel]
          }) as any) as MessageEvent)
        })
    )

    // send ping when ping method is called
    this.subscription.add(
      this.pingSubject.withLatestFrom(this.socket).subscribe(value => {
        const payload = value[0]
        const socket = value[1]
        socket.next((JSON.stringify({
          op: 'ping',
          payload
        }) as any) as MessageEvent)
      })
    )
  }

  connect () {
    this.connectSubject.next()
  }

  disconnect () {
    this.disconnectSubject.next()
  }

  ping (payload: string) {
    this.pingSubject.next(payload)
  }

  subscribe (channel: string): Rx.Observable<{}> {
    return Rx.Observable.create((observer: Rx.Observer<{}>) => {
      if (channel in this.subscriptions) {
        observer.error(
          new AlreadySubscribeError(`Channel ${channel} already subscribed`)
        )
        // tslint:disable-next-line:no-empty
        return () => {}
      }
      this.logger.info('Subscribed', channel)
      this.subscriptions[channel] = {
        lastID: null,
        observer
      }
      this.subscribeSubject.next(channel)

      return () => {
        this.logger.info('Unsubscribed', channel)
        delete this.subscriptions[channel]
        this.unsubscribeSubject.next(channel)
      }
    })
  }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/io/event-hub/EventHub.ts